import ProductIdentity from '../ProductIdentity';
import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSellerShipment, refreshSellerShipment } from '../../api';
import { deliveryProviderLabel } from '../../constants/deliveryProviders';
import {
  useSellerOrder,
  useSellerOrderAction,
  useSellerOrders,
  sellerOrdersKey,
} from '../../hooks/useSellerOrders';
import {
  SELLER_ORDER_FILTERS,
  customerOrderId,
  fulfillmentLabel,
  orderStatusLabel,
  sellerItemPrice,
  sellerItemQuantity,
  sellerItemTitle,
  sellerItemVariant,
  sellerOrderAmount,
  sellerOrderDate,
  sellerOrderError,
  sellerOrderFilterMatch,
  sellerOrderFulfillment,
  sellerOrderId,
  sellerOrderItems,
  sellerOrderStatus,
} from '../../utils/sellerOrderPresentation';
import './sellerOrders.css';

const money = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

function ShipmentActions({ order, sellerId }) {
  const shipment = order.fulfillment?.shipment;
  const queryClient = useQueryClient();
  const [acknowledged, setAcknowledged] = useState(false);
  const [notice, setNotice] = useState('');
  const mutation = useMutation({
    mutationFn: (action) =>
      action === 'create'
        ? createSellerShipment(shipment.id, acknowledged)
        : refreshSellerShipment(shipment.id),
    onSuccess: ({ data }) => {
      setAcknowledged(false);
      setNotice(
        data.pendingReconciliation
          ? 'Проверяем результат в Яндексе. Обновите статус позже: повторная заявка не отправляется.'
          : data.shipment?.providerRequestId
            ? 'Заявка сохранена. Статус обновлён.'
            : 'Данные обновлены.'
      );
      queryClient.invalidateQueries({ queryKey: sellerOrdersKey(sellerId) });
    },
  });
  if (!shipment || shipment.provider !== 'YANDEX') return null;
  if (shipment.consolidated) return <p className="seller-order-contract-note">Товары входят в общую посылку со склада MaxMarket. Подготовьте свою часть заказа; доставку оформляет склад.</p>;
  const created = Boolean(shipment.providerRequestId);
  const pending = shipment.externalCreateState === 'IN_PROGRESS';
  const cancelled = shipment.status === 'CANCELLED';
  const canCreate =
    !created &&
    !pending &&
    !cancelled &&
    ['PAID', 'PROCESSING', 'SHIPPED'].includes(sellerOrderStatus(order));
  const labels = {
    DRAFT: 'Черновик',
    PROCESSING: 'Заявка создана',
    READY_FOR_HANDOVER: 'Готово к передаче',
    IN_TRANSIT: 'В пути',
    OUT_FOR_DELIVERY: 'У получателя / в ПВЗ',
    DELIVERED: 'Доставлено',
    CANCELLED: 'Отменено',
    DELIVERY_FAILED: 'Проблема доставки',
    RETURNING: 'Возвращается',
    RETURNED: 'Возвращено',
  };
  return (
    <div className="seller-shipment-actions">
      <p>
        <strong>
          {pending
            ? 'Проверка создания заявки'
            : labels[shipment.status] || 'Статус уточняется'}
        </strong>
      </p>
      {created && (
        <p className="seller-shipment-reference">
          ID Яндекса: {shipment.providerRequestId}
        </p>
      )}
      {shipment.trackingNumber && (
        <p>Номер отслеживания: {shipment.trackingNumber}</p>
      )}
      {shipment.trackingUrl &&
        /^https:\/\/(?:dostavka\.yandex\.ru|(?:www\.)?yandex\.(?:ru|com))\//i.test(
          shipment.trackingUrl
        ) && (
          <p>
            <a href={shipment.trackingUrl} target="_blank" rel="noreferrer">
              Отследить доставку ↗
            </a>
          </p>
        )}
      {canCreate && (
        <>
          <p>
            Заявка будет создана в подключённом кабинете Яндекса. Убедитесь, что
            товар, упаковка и данные получателя готовы. Демонстрационная оплата
            не списывает деньги; заявка доставки настоящая.
          </p>
          <label className="seller-shipment-confirm">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={mutation.isPending}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />{' '}
            Подтверждаю создание настоящей заявки
          </label>
          <button
            className="seller-order-action"
            disabled={!acknowledged || mutation.isPending}
            onClick={() => mutation.mutate('create')}
          >
            {mutation.isPending
              ? 'Отправляем…'
              : shipment.externalCreateState === 'FAILED'
                ? 'Повторить создание заявки'
                : 'Создать заявку в Яндексе'}
          </button>
        </>
      )}
      {(created || pending) && !cancelled && (
        <button
          className="seller-order-action"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('refresh')}
        >
          {mutation.isPending ? 'Проверяем…' : 'Обновить статус доставки'}
        </button>
      )}
      {notice && <p role="status">{notice}</p>}
      {mutation.isError && (
        <p className="seller-order-error" role="alert">
          {mutation.error.response?.data?.error ||
            'Не удалось связаться с сервером. Повторите проверку статуса.'}
        </p>
      )}
    </div>
  );
}

function Action({ order, sellerId, onError }) {
  // SellerOrder.status is the canonical seller lifecycle. Server-side validation remains the
  // authority; this only exposes the next useful action in the live seller flow.
  const action =
    sellerOrderStatus(order) === 'PAID'
      ? { status: 'PROCESSING', label: 'Начать обработку' }
      : sellerOrderStatus(order) === 'PROCESSING'
        ? { status: 'SHIPPED', label: 'Отметить отправленным' }
        : null;
  const mutation = useSellerOrderAction(sellerId);
  if (!action) {
    return (
      <p className="seller-order-contract-note">
        Доступных действий на этом этапе нет.
      </p>
    );
  }
  const run = () =>
    mutation.mutate(
      {
        orderId: sellerOrderId(order),
        status: action.status,
      },
      { onError }
    );
  return (
    <button
      className="seller-order-action"
      onClick={run}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? 'Сохраняем…' : action.label}
    </button>
  );
}

function Items({ order, detail = false }) {
  return (
    <div
      className={detail ? 'seller-order-items detail' : 'seller-order-items'}
    >
      {sellerOrderItems(order).map((item, index) => {
        const quantity = sellerItemQuantity(item);
        return (
          <div
            className="seller-order-item"
            key={item.id || `${sellerItemTitle(item)}-${index}`}
          >
            <ProductIdentity product={item} title={sellerItemTitle(item)}>
              <span>
                {sellerItemVariant(item)
                  ? `Вариант / размер: ${sellerItemVariant(item)}`
                  : 'Вариант не указан'}
              </span>
              <span>
                {quantity} шт. × {money(sellerItemPrice(item))}
              </span>
            </ProductIdentity>
            <strong>{money(quantity * sellerItemPrice(item))}</strong>
          </div>
        );
      })}
    </div>
  );
}

function Detail({ order, sellerId, onBack, onActionError, actionError }) {
  return (
    <section className="seller-order-detail">
      <button className="seller-order-back" onClick={onBack}>
        ← К списку заказов
      </button>
      <div className="seller-order-heading">
        <div>
          <h3>Заказ №{customerOrderId(order) || sellerOrderId(order)}</h3>
          <span>{sellerOrderDate(order)}</span>
        </div>
        <div className="seller-order-badges">
          <span>{orderStatusLabel(sellerOrderStatus(order))}</span>
          <span>{fulfillmentLabel(sellerOrderFulfillment(order))}</span>
        </div>
      </div>
      <div className="seller-order-section">
        <h4>Товары продавца</h4>
        <Items order={order} detail />
      </div>
      {order.fulfillment?.shipment && (
        <div className="seller-order-section">
          <h4>Доставка</h4>
          <p>
            {deliveryProviderLabel(order.fulfillment.shipment.provider)} ·{' '}
            {order.fulfillment.shipment.deliveryMethod === 'COURIER'
              ? 'Курьер'
              : 'Пункт выдачи'}
          </p>
          <ShipmentActions
            key={order.fulfillment.shipment.id}
            order={order}
            sellerId={sellerId}
          />
        </div>
      )}
      <div className="seller-order-total">
        <span>Итого по вашим товарам</span>
        <strong>{money(sellerOrderAmount(order))}</strong>
      </div>
      {actionError && (
        <p className="seller-order-error">
          {sellerOrderError(actionError, 'изменить статус')}
        </p>
      )}
      <Action order={order} sellerId={sellerId} onError={onActionError} />
    </section>
  );
}

function OrdersTab({ sellerId, initialOrders = [], onError }) {
  const query = useSellerOrders(sellerId);
  const orders = query.data || initialOrders;
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const selected = orders.find(
    (order) => String(sellerOrderId(order)) === String(selectedId)
  );
  const detailQuery = useSellerOrder(sellerId, selectedId, selected);
  const [actionError, setActionError] = useState(null);
  const visibleOrders = useMemo(
    () => orders.filter((order) => sellerOrderFilterMatch(order, filter)),
    [orders, filter]
  );

  if (query.isError)
    return (
      <div className="seller-orders">
        <h2>Заказы</h2>
        <p className="seller-order-error">{sellerOrderError(query.error)}</p>
        <button className="seller-order-action" onClick={() => query.refetch()}>
          Повторить
        </button>
      </div>
    );
  if (selectedId) {
    if (detailQuery.isError)
      return (
        <div className="seller-orders">
          <button
            className="seller-order-back"
            onClick={() => setSelectedId(null)}
          >
            ← К списку заказов
          </button>
          <p className="seller-order-error">
            {sellerOrderError(detailQuery.error, 'загрузить заказ')}
          </p>
          <button
            className="seller-order-action"
            onClick={() => detailQuery.refetch()}
          >
            Повторить
          </button>
        </div>
      );
    if (detailQuery.isLoading && !detailQuery.data)
      return (
        <div className="seller-orders">
          <p>Загружаем заказ…</p>
        </div>
      );
    if (detailQuery.data)
      return (
        <div className="seller-orders">
          <Detail
            order={detailQuery.data}
            sellerId={sellerId}
            onBack={() => {
              setSelectedId(null);
              setActionError(null);
            }}
            onActionError={setActionError}
            actionError={actionError}
          />
        </div>
      );
  }

  return (
    <div className="seller-orders">
      <div className="seller-orders-title">
        <h2>Заказы</h2>
        <span>{orders.length} заказов</span>
      </div>
      <div
        className="seller-order-filters"
        role="group"
        aria-label="Фильтр статуса заказа"
      >
        {SELLER_ORDER_FILTERS.map((item) => (
          <button
            key={item.key}
            aria-pressed={filter === item.key}
            className={filter === item.key ? 'active' : ''}
            onClick={() => setFilter(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {query.isLoading && <p>Загружаем заказы…</p>}
      {!query.isLoading && !visibleOrders.length && (
        <div className="seller-orders-empty">
          <strong>
            {orders.length ? 'В этом статусе заказов нет' : 'Заказов пока нет'}
          </strong>
          <p>Новые заказы появятся здесь после оформления покупателями.</p>
        </div>
      )}
      <div className="seller-order-list">
        {visibleOrders.map((order) => (
          <article
            className="seller-order-card"
            key={sellerOrderId(order)}
            onClick={() => {
              setSelectedId(sellerOrderId(order));
              setActionError(null);
            }}
          >
            <div className="seller-order-card-top">
              <div>
                <strong>
                  Заказ №{customerOrderId(order) || sellerOrderId(order)}
                </strong>
                <span>{sellerOrderDate(order)}</span>
              </div>
              <div className="seller-order-badges">
                <span>{orderStatusLabel(sellerOrderStatus(order))}</span>
                <span>{fulfillmentLabel(sellerOrderFulfillment(order))}</span>
              </div>
            </div>
            <Items order={order} />
            <div className="seller-order-card-bottom">
              <span>
                {sellerOrderItems(order).reduce(
                  (sum, item) => sum + sellerItemQuantity(item),
                  0
                )}{' '}
                шт.
              </span>
              <strong>{money(sellerOrderAmount(order))}</strong>
              <button
                className="seller-order-open"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedId(sellerOrderId(order));
                }}
              >
                Подробнее
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export default OrdersTab;
